// SPDX-License-Identifier: BUSL-1.1

/**
 * Side-by-side JSON diff viewer for comparing config versions.
 *
 */

import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQueries, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { ConfigDiffSkeleton } from '@/components/skeletons';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

interface ConfigVersion {
  version: number;
  status: string;
  config: Record<string, unknown>;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-neutral-700 text-neutral-300',
  staged: 'bg-yellow-900/40 text-yellow-400',
  promoted: 'bg-green-900/30 text-green-400',
  rolled_back: 'bg-red-900/30 text-red-400',
};

function diffLines(a: string, b: string) {
  const linesA = a.split('\n');
  const linesB = b.split('\n');
  const max = Math.max(linesA.length, linesB.length);
  const left: { text: string; type: 'removed' | 'same' }[] = [];
  const right: { text: string; type: 'added' | 'same' }[] = [];
  for (let i = 0; i < max; i++) {
    const la = linesA[i] ?? '';
    const lb = linesB[i] ?? '';
    const same = la === lb;
    left.push({ text: la, type: same ? 'same' : 'removed' });
    right.push({ text: lb, type: same ? 'same' : 'added' });
  }
  return { left, right };
}

export function ConfigDiffPage() {
  const { key } = useParams<{ key: string }>();
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const { data: detail } = useQuery({
    queryKey: ['configs', key],
    queryFn: () => apiFetch<{ versions: ConfigVersion[] }>(`/configs/${key}`),
    enabled: !!key,
  });
  const versions = detail?.versions ?? [];

  const [qFrom, qTo] = useQueries({
    queries: [
      { queryKey: ['config-ver', key, from], queryFn: () => apiFetch<ConfigVersion>(`/configs/${key}/versions/${from}`), enabled: !!from },
      { queryKey: ['config-ver', key, to], queryFn: () => apiFetch<ConfigVersion>(`/configs/${key}/versions/${to}`), enabled: !!to },
    ],
  });

  const fromJson = qFrom.data ? JSON.stringify(qFrom.data.config, null, 2) : '';
  const toJson = qTo.data ? JSON.stringify(qTo.data.config, null, 2) : '';
  const diff = useMemo(() => diffLines(fromJson, toJson), [fromJson, toJson]);

  const setVersion = (side: 'from' | 'to', v: string) => {
    const next = new URLSearchParams(params);
    next.set(side, v);
    setParams(next);
  };

  const bgFor = (type: string) =>
    type === 'removed' ? 'bg-red-900/20' : type === 'added' ? 'bg-green-900/20' : '';

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center gap-3">
        <Link to={`/configs/${key}`} className="text-sm text-neutral-400 hover:text-neutral-200">&larr; Back</Link>
        <h1 className="font-mono text-xl font-bold text-neutral-100">{key} &mdash; Diff</h1>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-neutral-400">
          <span>From</span>
          <Select value={from} onValueChange={(v) => v && setVersion('from', v)}>
            <SelectTrigger className="w-[100px]"><SelectValue placeholder="Version" /></SelectTrigger>
            <SelectContent>
              {versions.map((v) => (
                <SelectItem key={v.version} value={String(v.version)}>v{v.version}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-400">
          <span>To</span>
          <Select value={to} onValueChange={(v) => v && setVersion('to', v)}>
            <SelectTrigger className="w-[100px]"><SelectValue placeholder="Version" /></SelectTrigger>
            <SelectContent>
              {versions.map((v) => (
                <SelectItem key={v.version} value={String(v.version)}>v{v.version}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(qFrom.isLoading || qTo.isLoading) && <ConfigDiffSkeleton />}

      {qFrom.data && qTo.data && (
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-px overflow-hidden rounded border border-neutral-800">
          {[{ label: `v${from}`, status: qFrom.data.status, lines: diff.left },
            { label: `v${to}`, status: qTo.data.status, lines: diff.right }].map((panel) => (
            <div key={panel.label} className="flex flex-col overflow-hidden bg-neutral-900">
              <div className="flex items-center gap-2 border-b border-neutral-800 px-4 py-2">
                <span className="font-mono text-sm font-medium text-neutral-200">{panel.label}</span>
                <span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_COLORS[panel.status] ?? ''}`}>{panel.status}</span>
              </div>
              <pre className="flex-1 overflow-auto p-4 font-mono text-sm leading-6 text-neutral-300">
                {panel.lines.map((l, i) => (
                  <div key={i} className={bgFor(l.type)}>{l.text || '\u00A0'}</div>
                ))}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
